<!-- @format -->
<template>
    <div class="collateral-preview-popup">
        <close-svg class="close-btn" @click.native="closePopup" />
        <div class="collateral">
            <div class="collateral-preview-image">
                <iframe
                    v-if="isPdf"
                    ref="iframe"
                    class="pdf-preview-container"
                    :src="collateral.url"
                >
                </iframe>
                <div
                    v-else-if="collateral.previewImageUrl"
                    class="preview-image"
                    :style="{ 'background-image': `url('${collateral.previewImageUrl}')` }"
                ></div>
                <app-file-icon v-else class="svg-container" :type="collateral.fileType" />
            </div>
            <div class="collateral-details">
                <div class="file-details">
                    <div class="file-name">
                        <p><strong>Filename:</strong> {{ collateral.name }}</p>
                    </div>
                    <p v-if="collateral.fileSizeInBytes">
                        <strong>File Size:</strong>
                        {{ collateral.fileSizeInBytes | bytesToLargestUnit }}
                    </p>
                    <p><strong>File Extension:</strong> {{ collateral.fileExtension }}</p>
                </div>
                <div class="file-meta">
                    <p><strong>Uploaded on:</strong> {{ collateral.createdAt }}</p>
                    <p v-if="collateral.uploadedByUser && isEditable">
                        <strong>Uploaded by:</strong> {{ collateral.uploadedByUser.name }}
                    </p>
                </div>
            </div>
            <div class="collateral-categories">
                <div v-if="collateral.categories.length > 0" class="categories-container">
                    <div
                        v-for="(category, idx) in collateral.categories"
                        :key="category.id"
                        class="category"
                    >
                        <span>
                            {{
                                category.name +
                                (collateral.categories.length - 1 !== idx ? ',' : '')
                            }}
                        </span>
                    </div>
                </div>
                <p v-else>No Categories</p>
            </div>
            <div class="actions-container">
                <app-button
                    v-if="isEditable"
                    class="action transparent-button edit-btn"
                    @click="openCollateralEditWidget"
                >
                    Edit
                </app-button>
                <app-button class="action transparent-button" @click="openFile">
                    <span v-if="collateral.fileType === 'image'">View Full Image</span>
                    <span v-else-if="collateral.fileType === 'video'">View Video</span>
                    <span v-else-if="collateral.fileType === 'document'">View Document</span>
                    <span v-else-if="collateral.fileType === 'excel'">View Spreadsheet</span>
                    <span v-else-if="collateral.fileType === 'presentation'">
                        View Presentation
                    </span>
                    <span v-else-if="collateral.fileType === 'pdf'">View PDF</span>
                    <span v-else-if="collateral.fileType === 'link'">View Link</span>
                    <span v-else>View File</span>
                </app-button>
                <app-button
                    v-for="action in actions"
                    :key="action.label"
                    :class="['action', action.class]"
                    @click.native="invokeAction(action)"
                >
                    {{ action.label }}
                </app-button>
            </div>
        </div>
    </div>
</template>

<script>
const AppButton = () => import('@/components/common/app-button');
const CloseSvg = () => import('@/components/svg/close-svg');
const AppFileIcon = () => import('@/components/common/app-file-icon');

import EventBus from '@/scripts/event-bus.js';
import transactionHistoryVue from '../account/transaction-history.vue';
import log from '@/scripts/logger';

export default {
    components: {
        'app-button': AppButton,
        'close-svg': CloseSvg,
        'app-file-icon': AppFileIcon,
    },

    props: {
        collateralId: {
            type: Number,
            required: true,
        },

        actions: {
            type: Object,
            default: null,
        },
    },

    data() {
        return {
            collateral: {
                id: null,
                createdAt: null,
                updatedAt: null,
                previewImageUrl: null,
                url: null,
                fileExtension: null,
                fileType: null,
                fileSizeInBytes: null,
                name: null,
                categories: [],
                clientId: null,
                agencyId: null,
                masterAgencyId: null,
                uploadedByUser: null,
            },
        };
    },

    computed: {
        clientId() {
            return this.$store.state.context.client.id;
        },

        isClientUser() {
            return this.$store.getters.isClientUser;
        },

        isMasterAgencyUser() {
            return this.$store.getters.isMasterAgencyUser;
        },

        isAgencyUser() {
            return this.$store.getters.isAgencyUser;
        },

        isOrganizationUser() {
            return this.$store.getters.isOrganizationUser;
        },

        isEditable() {
            if (this.collateral.masterAgencyId) {
                return this.isOrganizationUser || this.isMasterAgencyUser;
            } else if (this.collateral.agencyId) {
                return this.isOrganizationUser || this.isMasterAgencyUser || this.isAgencyUser;
            } else if (this.collateral.clientId) {
                return true;
            } else {
                return this.isOrganizationUser;
            }
        },

        isPdf() {
            return this.collateral.fileExtension === 'pdf';
        },
    },

    created() {
        this.getData();
    },

    methods: {
        openCollateralEditWidget() {
            EventBus.$emit('open-widget', {
                name: 'collateral-edit-widget',
                options: {
                    collateralId: this.collateral.id,
                },
            });

            this.closePopup();
        },

        getData() {
            this.getCollateral();
        },

        getCollateral() {
            return new Promise((resolve, reject) => {
                var expand =
                    '$expand=Categories,UploadedByUser($select=Id,FirstName,LastName,ProfileImageUrl,MasterAgencyId,AgencyId,ClientId)';
                var filter = `$filter=Id eq ${this.collateralId}`;
                var select =
                    '$select=Id,PreviewImageUrl,Url,FileExtension,FileSizeInBytes,FileType,Name,ClientId,AgencyId,MasterAgencyId,CreatedAt,UpdatedAt,UploadedByUser';
                var query = `/service/Collaterals?${expand}&${filter}&${select}`;

                this.$api
                    .get(query)
                    .then((response) => {
                        var responseValue = response.data.value[0];

                        this.collateral.id = responseValue.Id;
                        this.collateral.previewImageUrl = responseValue.PreviewImageUrl;
                        this.collateral.url = responseValue.Url;
                        this.collateral.fileExtension = responseValue.FileExtension;
                        this.collateral.fileSizeInBytes = responseValue.FileSizeInBytes;
                        this.collateral.fileType = (responseValue.FileType || '').toLowerCase();
                        this.collateral.name = responseValue.Name;
                        this.collateral.clientId = responseValue.ClientId;
                        this.collateral.agencyId = responseValue.AgencyId;
                        this.collateral.masterAgencyId = responseValue.MasterAgencyId;
                        this.collateral.categories = this.hydrateCategories(
                            responseValue.Categories
                        );
                        this.collateral.createdAt = this.$moment(responseValue.CreatedAt)
                            .local()
                            .format('MMMM, DD, YYYY');
                        this.collateral.updatedAt = this.$moment(responseValue.UpdatedAt)
                            .local()
                            .format('MMMM, DD, YYYY');
                        this.collateral.uploadedByUser = responseValue.UploadedByUser
                            ? this.hydrateUser(responseValue.UploadedByUser)
                            : null;

                        resolve();
                    })
                    .catch((error) => {
                        log.error('Failed to get collateral', error);
                        reject();
                    });
            });
        },

        hydrateUser(user) {
            if (user) {
                return {
                    id: user.Id,
                    name: this.$helpers.getName(user.FirstName, user.LastName),
                    profileImageUrl: user.ProfileImageUrl || '',
                    masterAgencyId: user.MasterAgencyId,
                    agencyId: user.AgencyId,
                    clientId: user.ClientId,
                };
            }

            return {
                name: 'No name',
                profileImageUrl: null,
            };
        },

        hydrateCategories(categories) {
            return categories.map(({ Id, Name }) => ({
                id: Id,
                name: Name,
            }));
        },

        invokeAction(action) {
            action.action(action.item);
            this.closePopup();
        },

        openFile() {
            window.open(this.collateral.url, '_blank');
        },

        closePopup() {
            this.$emit('close');
        },
    },
};
</script>

<style>
.collateral-preview-popup {
    max-width: 803px;
    width: 100%;
    color: #ffffff;
    height: 100%;
    padding: 70px 20px 40px 20px;
    position: static;
    overflow: auto;
}

.collateral-preview-popup .collateral-preview-image {
    width: 100%;
    height: 400px;
    margin-bottom: 18px;
    display: flex;
}

.collateral-preview-popup .collateral-preview-image .pdf-preview-container {
    width: 100%;
}

.collateral-preview-popup .collateral-preview-image .svg-container {
    text-align: center;
    width: 100%;
}

.collateral-preview-popup .collateral-preview-image .svg-container svg {
    height: 400px;
    width: auto;
}

.collateral-preview-popup .collateral-preview-image .preview-image {
    height: 100%;
    width: 100%;
    background-size: contain;
    background-repeat: no-repeat;
    background-position: center;
    border-radius: 6px;
}

.collateral-preview-popup .collateral-details {
    display: flex;
    justify-content: space-between;
    width: 100%;
    margin-bottom: 16px;
}

.collateral-preview-popup p strong {
    color: #ffffff;
}

.collateral-preview-popup .collateral-details .file-details {
    display: flex;
    flex-direction: column;
    padding-right: 10px;
}

.collateral-preview-popup .collateral-details .file-details .file-name {
    margin-bottom: 10px;
    flex-wrap: wrap;
}

.collateral-preview-popup .collateral-details .file-details .file-name.edit-shown {
    align-items: flex-start;
}

.collateral-preview-popup .collateral-details .file-details .file-name,
.collateral-preview-popup .categories-edit {
    display: flex;
    align-items: center;
}

.collateral-preview-popup .collateral-details .file-details .file-name p,
.collateral-preview-popup .categories-edit p {
    margin-bottom: 0;
    margin-right: 22px;
}

.collateral-preview-popup .save-button {
    margin-left: 10px;
}

.collateral-preview-popup .edit-button,
.collateral-preview-popup .save-button {
    font-size: 18px;
}

.collateral-preview-popup .edit-button div,
.collateral-preview-popup .save-button div {
    display: flex;
    align-items: flex-start;
}

.collateral-preview-popup .edit-button svg {
    margin-left: 10px;
}

.collateral-preview-popup .edit-button svg,
.collateral-preview-popup .save-button svg {
    height: 15px;
    fill: #ffffff;
}

.collateral-preview-popup .edit-button {
    border: 2px solid #ffffff;
    background: transparent;
}

.collateral-preview-popup .collateral-categories {
    padding-bottom: 20px;
    border-bottom: 1px solid #ffffff;
}

.collateral-preview-popup .collateral-categories .categories-edit {
    margin-bottom: 8px;
}

.collateral-preview-popup .collateral-categories .categories-container {
    display: flex;
    flex-wrap: wrap;
    white-space: pre-wrap;
    text-transform: capitalize;
}

.collateral-preview-popup .actions-container {
    padding: 10px 0px;
    display: flex;
    justify-content: flex-end;
}

.collateral-preview-popup .actions-container .action {
    margin-left: 15px;
}

.collateral-preview-popup .actions-container .transparent-button {
    border: 2px solid #ffffff;
    background: transparent;
}

.collateral-preview-popup .loading-container {
    display: flex;
    justify-content: center;
    align-items: center;
    width: 100%;
}

.read-only .collateral-preview-popup .edit-btn {
    display: none;
}
</style>
